import { BrowserInfo } from '@ms/yammer-telemetry/dist/browser';

import { ConvertibleObjectType } from '../../normalization/types';
import { Feed, Group, Thread } from '../../state/types';

import { RealtimeErrorEventProperties } from './errorEvent.types';
import {
  BaseTelemetryEvent,
  PropertyCheckEventProperties,
  TelemetryEventType,
  TelemetryPropertyValueType,
} from './types';

export interface InfoEvent extends BaseTelemetryEvent {
  readonly type: TelemetryEventType.Info;
  readonly properties: Record<string | keyof InfoEventProperties, TelemetryPropertyValueType>;
}

export type InfoEventProperties =
  | Pick<AnalyticsV2EventErroredProperties, keyof AnalyticsV2EventErroredProperties>
  | Pick<AuthenticationInfoEventProperties, keyof AuthenticationInfoEventProperties>
  | Pick<AzureVideoPlayerEventProperties, keyof AzureVideoPlayerEventProperties>
  | Pick<BootstrapInfoEventProperties, keyof BootstrapInfoEventProperties>
  | Pick<FeedbackInfoEventProperties, keyof FeedbackInfoEventProperties>
  | Pick<FeedThreadsDuplicatedInfoEventProperties, keyof FeedThreadsDuplicatedInfoEventProperties>
  | Pick<LegacyRouteTranslationFailedInfoEventProperties, keyof LegacyRouteTranslationFailedInfoEventProperties>
  | Pick<LivePersonaCardInfoEventProperties, keyof LivePersonaCardInfoEventProperties>
  | Pick<PageGroupLoadInfoEventProperties, keyof PageGroupLoadInfoEventProperties>
  | Pick<PropertyCheckEventProperties, keyof PropertyCheckEventProperties>
  | Pick<
      RealtimeFeedLoadNewerRequestSkippedInfoEventProperties,
      keyof RealtimeFeedLoadNewerRequestSkippedInfoEventProperties
    >
  | Pick<
      RealtimeGroupsUnseenCountRequestSkippedInfoEventProperties,
      keyof RealtimeGroupsUnseenCountRequestSkippedInfoEventProperties
    >
  | Pick<
      RealtimeGroupUnseenCountRequestSkippedInfoEventProperties,
      keyof RealtimeGroupUnseenCountRequestSkippedInfoEventProperties
    >
  | Pick<
      RealtimeInboxUnreadCountRequestSkippedInfoEventProperties,
      keyof RealtimeInboxUnreadCountRequestSkippedInfoEventProperties
    >
  | Pick<RealtimeInfoEventProperties, keyof RealtimeInfoEventProperties>
  | Pick<
      RealtimeNotificationsUnseenCountRequestSkippedInfoEventProperties,
      keyof RealtimeNotificationsUnseenCountRequestSkippedInfoEventProperties
    >
  | Pick<RealtimeRepliesNotReceivedEventProperties, keyof RealtimeRepliesNotReceivedEventProperties>
  | Pick<SetImageRenderingCookieSucceededInfoEventProperties, keyof SetImageRenderingCookieSucceededInfoEventProperties>
  | Pick<SharePointFeedPrefetchedInfoEventProperties, keyof SharePointFeedPrefetchedInfoEventProperties>
  | Pick<StreamPlayerInfoEventProperties, keyof StreamPlayerInfoEventProperties>
  | Pick<TeamsPlayerInfoEventProperties, keyof TeamsPlayerInfoEventProperties>
  | Pick<SurveyInfoEventProperties, keyof SurveyInfoEventProperties>
  | Pick<TeamsClientMetadataInfoEventProperties, keyof TeamsClientMetadataInfoEventProperties>
  | Pick<ThemeUpdatedEventProperties, keyof ThemeUpdatedEventProperties>
  | Pick<WebBootstrapInfoEventProperties, keyof WebBootstrapInfoEventProperties>
  | Pick<WriteHostingCookieInfoEventProperties, keyof WriteHostingCookieInfoEventProperties>;

export interface AnalyticsV2EventErroredProperties {
  readonly name: string;
  readonly errorMessage: string;
}

export interface AuthenticationInfoEventProperties {
  readonly authenticatorType: string;
  readonly scopes: string[];
}

export interface AzureVideoPlayerEventProperties {
  readonly videoId: string;
  readonly groupId?: Group['id'];
  readonly errorCode?: string;
}

export interface BootstrapInfoEventProperties {
  readonly isExecutingInIFrame: boolean;
}

export interface FeedbackInfoEventProperties {
  readonly type: 'proactive' | 'reactive';
}

export interface FeedThreadsDuplicatedInfoEventProperties {
  readonly threadIdsWithDuplicates: string;
}

export interface LegacyRouteTranslationFailedInfoEventProperties {
  readonly reason?: 'noHashUrl' | 'unknownEntityType' | 'noGlobalId';
  readonly type?: ConvertibleObjectType;
}

export interface LivePersonaCardInfoEventProperties {
  readonly clientCorrelationId: string;
  readonly clientType: string;
}

export interface PageGroupLoadInfoEventProperties {
  readonly base64GroupId: Group['id'];
}

export interface RealtimeFeedLoadNewerRequestSkippedInfoEventProperties {
  readonly attempts?: number;
  readonly feedType?: Feed['type'];
  readonly feedSubtype?: Feed['subtype'];
  readonly feedEntityId?: Feed['entityId'];
  readonly reason: RequestSkippedReason;
}

export interface RealtimeGroupsUnseenCountRequestSkippedInfoEventProperties {
  readonly reason: RequestSkippedReason;
}

export interface RealtimeGroupUnseenCountRequestSkippedInfoEventProperties {
  readonly groupId: Group['id'];
  readonly threadCount: number;
  readonly reason: RequestSkippedReason;
}

export interface RealtimeInboxUnreadCountRequestSkippedInfoEventProperties {
  readonly reason: RequestSkippedReason;
}

export interface RealtimeNotificationsUnseenCountRequestSkippedInfoEventProperties {
  readonly reason: RequestSkippedReason;
}

export interface RealtimePublishEventProperties {
  readonly channel: string;
}

export interface RealtimeRepliesNotReceivedEventProperties {
  readonly threadId: Thread['id'];
  readonly totalRepliesCount: number;
  readonly shownRepliesCount: number;
  readonly newerRepliesCount: number;
}

export type RealtimeInfoEventProperties = RealtimeErrorEventProperties | RealtimePublishEventProperties;

export interface SetImageRenderingCookieSucceededInfoEventProperties {
  readonly duration: number;
  readonly expiration: number;
}

export interface SharePointFeedPrefetchedInfoEventProperties {
  readonly feedType: Feed['type'];
  readonly feedSubtype?: Feed['subtype'];
  readonly feedEntityId?: Feed['entityId'];
}

export interface StreamPlayerInfoEventProperties {
  readonly broadcastId?: string;
  readonly messageName?: string;
  readonly clientSessionId?: string;
  readonly eventDataName?: string;
  readonly errorCode?: string;
  readonly message?: string;
  readonly responseCode?: string;
  readonly isUsingStreamTokenAuth?: boolean;
  readonly resource?: string;
}

export interface SurveyInfoEventProperties {
  readonly sessionId: string;
  readonly campaignIds: string[];
}

export interface TeamsPlayerInfoEventProperties {
  readonly broadcastId?: string;
  readonly messageEventId?: string;
  readonly payloadEventId?: string;
  readonly errorCode?: string;
  readonly errorMessage?: string;
  readonly message?: string;
  readonly responseCode?: string;
  readonly resource?: string;
  readonly correlationId?: string;
}

export interface TeamsClientMetadataInfoEventProperties {
  readonly teamsClientMetadata: string; // Format - sid:<SessionId>::asid:<AppSessionId>::rid:<RingId>::tid:<TeamId>::cid:<ChannelId>::gid:<GroupId>
}

export interface ThemeUpdatedEventProperties {
  readonly isDarkMode: boolean;
}

export interface WebBootstrapInfoEventProperties extends BrowserInfo {
  readonly isInTeamsApp?: boolean;
  readonly urlRootPath: string;
}

export interface WriteHostingCookieInfoEventProperties {
  readonly contextMessage?: string;
  readonly name: string;
  readonly statusMessage: WriteHostingCookieStatus;
}

export type WriteHostingCookieStatus = 'skipped' | 'succeeded' | 'failed';

export enum RequestSkippedReason {
  CountAboveVisibleLimit = 'countAboveVisibleLimit',
  FeedNotLoaded = 'feedNotLoaded',
  FeedPaused = 'feedPaused',
  GroupFeedSubscribed = 'groupFeedSubscribed',
  InboxFeedSubscribed = 'inboxFeedSubscribed',
  MessageAlreadyLoaded = 'messageAlreadyLoaded',
  NewerRequestFulfilled = 'newerRequestFulfilled',
  PreviousRequestPending = 'previousRequestPending',
  ReplyOnStaticallySortedFeed = 'ReplyOnStaticallySortedFeed',
}

export enum InfoEventName {
  AnalyticsV2EventErrored = 'analytics_v2_event_errored',
  AuthBootstrapInfo = 'auth_bootstrap_info',
  AzureVideoPlayerErrored = 'azure_video_player_errored',
  AzureVideoPlayerInitialize = 'azure_video_player_initialize',
  AzureVideoPlayerWaiting = 'azure_video_player_waiting',
  BootstrapStarted = 'bootstrap_started',
  DelayLoadRenderers = 'delay_load_renderers',
  FeedThreadsDuplicated = 'feed_threads_duplicated',
  HostingCookieSet = 'hosting_cookie_set',
  LegacyRouteTranslationFailed = 'legacy_route_translation_failed',
  LivePersonaCardInfo = 'live_persona_card_info',
  PageGroupLoaded = 'page_group_loaded',
  RealtimeArtieDisconnected = 'realtime_artie_disconnected',
  RealtimeFeedLoadNewerRequestSkipped = 'realtime_feed_load_newer_request_skipped',
  RealtimeGroupUnseenCountRequestSkipped = 'realtime_group_unseen_count_request_skipped',
  RealtimeGroupsUnseenCountRequestSkipped = 'realtime_groups_unseen_count_request_skipped',
  RealtimeInboxUnreadCountRequestSkipped = 'realtime_inbox_unread_count_request_skipped',
  RealtimeNotificationsUnseenCountRequestSkipped = 'realtime_notifications_unseen_count_request_skipped',
  RealtimeHandshakeFailed = 'realtime_handshake_failed',
  RealtimePermanentlyDisconnected = 'realtime_permanently_disconnected',
  RealtimePublished = 'realtime_published',
  RealtimeReconfiguringCometD = 'realtime_reconfiguring_cometd',
  RealtimeReconnected = 'realtime_reconnected',
  RealtimeReconnectedAfterPermanentDisconnect = 'realtime_reconnected_after_permanent_disconnect',
  RealtimeRepliesNotReceived = 'realtime_replies_not_received',
  RealtimeRequestingAnotherURI = 'realtime_requesting_another_uri',
  SetImageRenderingCookieSucceeded = 'set_image_rendering_cookie_succeeded',
  SharePointFeedPrefetched = 'feed_prefetched',
  StreamPlayerDataLoaded = 'stream_player_data_loaded',
  StreamPlayerErrored = 'stream_player_errored',
  StreamPlayerInitialize = 'stream_player_initialize',
  StreamPlayerNoResource = 'stream_player_no_resource',
  StreamPlayerSignInFailed = 'stream_player_sign_in_failed',
  StreamPlayerSignInIncomplete = 'stream_player_sign_in_incomplete',
  StreamPlayerTokenException = 'stream_player_token_exception',
  TeamsPlayerDataLoaded = 'teams_player_data_loaded',
  TeamsPlayerErrored = 'teams_player_errored',
  TeamsPlayerInitialize = 'teams_player_initialize',
  TeamsPlayerHeartbeat = 'teams_player_heartbeat',
  TeamsPlayerMetadata = 'teams_player_metadata',
  TeamsPlayerNoResource = 'teams_player_no_resource',
  TeamsPlayerSignInFailed = 'teams_player_sign_in_failed',
  TeamsPlayerSignInComplete = 'teams_player_sign_in_complete',
  TeamsPlayerTokenException = 'teams_player_token_exception',
  TeamsQnALoadedFromCache = 'teams_qna_loaded_from_cache',
  TeamsAppLoadedFromCache = 'teams_app_loaded_from_cache',
  TeamsAppSwitchedOnLoadFromCache = 'teams_app_switched_on_load_from_cache',
  TeamsClientMetadata = 'teams_client_metadata',
  TeamsMeetingFeedSubtypeChanged = 'teams_meeting_feed_subtype_changed',
  TestInfoEvent = 'test_info_event',
  ThemeUpdated = 'theme_updated',
}
